<template>
  <header class="header shadow-sm">
    <div class="logo">
      <img :src="logoImg" alt="logo" class="logo-img" />
      <span>莎士比耶</span>
    </div>
    <div class="center-title" role="heading" aria-level="2">{{ pageTitle }}</div>
    <div class="header-right">
      <div class="user-info">
        <span class="username">{{ userInfo.username }}</span>
        <span class="points-label">余额：{{ balanceText }} 元</span>
        <el-button class="logout-btn" size="large" @click="openRechargeDialog">充值余额</el-button>
        <el-button type="danger" size="small" class="logout-btn" @click="handleLogout">退出</el-button>
      </div>
    </div>
  </header>
  <el-dialog
    v-model="rechargeDialogVisible"
    title="账户充值"
    width="460px"
    class="recharge-dialog"
    :close-on-click-modal="false"
    @closed="handleRechargeDialogClosed"
  >
    <div class="recharge-panel">
      <div class="recharge-intro">
        <div class="recharge-intro-title">微信扫码安全充值</div>
        <div class="recharge-intro-desc">选择金额后生成二维码，支付成功自动到账</div>
      </div>
      <div class="recharge-row">
        <span class="recharge-label">充值金额</span>
        <el-radio-group v-model="rechargeAmount" class="recharge-amount-group" :disabled="creatingRecharge || pollingRecharge">
          <el-radio-button :label="10">10元</el-radio-button>
          <el-radio-button :label="30">30元</el-radio-button>
          <el-radio-button :label="50">50元</el-radio-button>
          <el-radio-button :label="100">100元</el-radio-button>
        </el-radio-group>
        <div class="custom-amount-wrap">
          <el-input-number
            v-model="customRechargeAmount"
            :min="0.01"
            :step="0.01"
            :precision="2"
            :controls="false"
            :disabled="creatingRecharge || pollingRecharge"
            class="custom-amount-input"
            placeholder="自定义金额（最低0.01元）"
          />
          <span class="custom-amount-unit">元</span>
        </div>
        <div class="custom-amount-tip">填写后将优先使用自定义金额（最低 0.01 元）</div>
      </div>
      <div class="recharge-actions">
        <el-button
          type="primary"
          class="recharge-create-btn"
          :loading="creatingRecharge"
          :disabled="pollingRecharge"
          @click="createRechargeOrder"
        >
          生成支付二维码
        </el-button>
      </div>
      <div v-if="rechargeCodeUrl" class="qr-wrap">
        <img class="qr-img" :src="rechargeQrImageUrl" alt="充值二维码" />
        <div class="qr-tip">请使用微信扫一扫支付</div>
        <div class="qr-tip">订单号：{{ currentOutTradeNo }}</div>
        <div class="qr-tip" v-if="rechargeExpireAt">有效期至：{{ rechargeExpireAt }}</div>
        <div class="qr-state" v-if="pollingRecharge">等待支付中...</div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import request from '@/api/request'
import logoImg from '../image/logo.jpg'
import { getAppPageTitle } from '@/config/slowDramaWorkflows'

const router = useRouter()
const route = useRoute()
const userInfo = ref({ username: '' })
const balance = ref(0)
const balanceText = computed(() => Number(balance.value || 0).toFixed(2))
const rechargeDialogVisible = ref(false)
const rechargeAmount = ref(30)
const customRechargeAmount = ref(null)
const creatingRecharge = ref(false)
const pollingRecharge = ref(false)
const rechargeCodeUrl = ref('')
const currentOutTradeNo = ref('')
const rechargeExpireAt = ref('')
let rechargePollTimer = null
const rechargeQrImageUrl = computed(() => {
  const url = String(rechargeCodeUrl.value || '').trim()
  if (!url) return ''
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}`
})

const pageTitle = computed(() => getAppPageTitle(route))

const getUserInfo = () => {
  const storedUserInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo')
  if (storedUserInfo) {
    const userInfoData = JSON.parse(storedUserInfo)
    userInfo.value = userInfoData
    // 统一使用人民币余额字段，兼容旧缓存
    balance.value = Number(userInfoData.balance_cny ?? 0)
  }
}

const syncBalanceFromServer = async () => {
  try {
    const response = await request.post('/user/refresh_user_data')
    if (!response || !Object.prototype.hasOwnProperty.call(response, 'balance_cny')) {
      return
    }
    const nextBalance = Number(response.balance_cny || 0)
    balance.value = nextBalance

    const storedUserInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo')
    if (storedUserInfo) {
      const userInfoData = JSON.parse(storedUserInfo)
      userInfoData.balance_cny = nextBalance
      if (localStorage.getItem('userInfo')) {
        localStorage.setItem('userInfo', JSON.stringify(userInfoData))
      }
      if (sessionStorage.getItem('userInfo')) {
        sessionStorage.setItem('userInfo', JSON.stringify(userInfoData))
      }
    }
  } catch (error) {
    // 静默失败，避免影响主界面渲染
  }
}

const handleLogout = () => {
  localStorage.removeItem('userInfo')
  localStorage.removeItem('token')
  sessionStorage.removeItem('userInfo')
  router.push('/login')
}

const stopRechargePolling = () => {
  if (rechargePollTimer) {
    clearInterval(rechargePollTimer)
    rechargePollTimer = null
  }
  pollingRecharge.value = false
}

const pollRechargeStatus = () => {
  if (!currentOutTradeNo.value) return
  stopRechargePolling()
  pollingRecharge.value = true
  rechargePollTimer = setInterval(async () => {
    try {
      const response = await request.post('/user/recharge_query', {
        out_trade_no: currentOutTradeNo.value
      })
      const order = response?.order || {}
      const status = Number(order.status ?? -1)
      if (status === 1) {
        stopRechargePolling()
        ElMessage.success('充值成功，余额已更新')
        await syncBalanceFromServer()
        rechargeDialogVisible.value = false
        return
      }
      if (status === 2) {
        stopRechargePolling()
        ElMessage.warning('订单已过期，请重新生成二维码')
        return
      }
      if (status === 3 || status === 4) {
        stopRechargePolling()
        ElMessage.warning(order.status_text || '订单状态异常，请重试')
      }
    } catch (error) {
      // 轮询期间网络波动不打断流程
    }
  }, 3000)
}

const createRechargeOrder = async () => {
  if (creatingRecharge.value) return
  const customAmount = Number(customRechargeAmount.value || 0)
  const amountToPay = customAmount > 0 ? customAmount : Number(rechargeAmount.value || 0)
  if (!Number.isFinite(amountToPay) || amountToPay < 0.01) {
    ElMessage.warning('充值金额最低为0.01元')
    return
  }
  creatingRecharge.value = true
  try {
    const response = await request.post('/user/recharge_create_native', {
      amount_cny: amountToPay.toFixed(2)
    })
    rechargeCodeUrl.value = String(response.code_url || '')
    currentOutTradeNo.value = String(response.out_trade_no || '')
    rechargeExpireAt.value = String(response.expire_at || '')
    if (!rechargeCodeUrl.value || !currentOutTradeNo.value) {
      ElMessage.error('生成支付二维码失败，请重试')
      return
    }
    pollRechargeStatus()
  } catch (error) {
    // 错误提示由 request 统一处理
  } finally {
    creatingRecharge.value = false
  }
}

const openRechargeDialog = () => {
  rechargeDialogVisible.value = true
}

const handleRechargeDialogClosed = () => {
  stopRechargePolling()
  rechargeCodeUrl.value = ''
  currentOutTradeNo.value = ''
  rechargeExpireAt.value = ''
  customRechargeAmount.value = null
}

const onBalanceUpdated = (event) => {
  balance.value = Number(event.detail.balance_cny ?? 0)

  const storedUserInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo')
  if (storedUserInfo) {
    const userInfoData = JSON.parse(storedUserInfo)
    userInfoData.balance_cny = Number(event.detail.balance_cny ?? 0)

    if (localStorage.getItem('userInfo')) {
      localStorage.setItem('userInfo', JSON.stringify(userInfoData))
    }
    if (sessionStorage.getItem('userInfo')) {
      sessionStorage.setItem('userInfo', JSON.stringify(userInfoData))
    }
  }
}

onMounted(() => {
  getUserInfo()
  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
  if (token) {
    syncBalanceFromServer()
  }
  window.addEventListener('balanceUpdated', onBalanceUpdated)
})

onBeforeUnmount(() => {
  window.removeEventListener('balanceUpdated', onBalanceUpdated)
  stopRechargePolling()
})
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 28px;
  background: #fff;
  border-bottom: 1px solid var(--border-color);
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-img {
  height: 50px;
  width: auto;
  display: block;
  border-radius: 4px;
}

.logo span {
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.center-title {
  flex: 1;
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: 0.04em;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.username {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
}

.points-label {
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.points-popover {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.points-title {
  text-align: center;
  font-size: 18px;
  color: var(--text-secondary);
}

.points-img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.logout-btn {
  border-radius: 8px;
}

.recharge-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.recharge-intro {
  border: 1px solid #e5eefc;
  background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 12px 14px;
}

.recharge-intro-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e3a8a;
}

.recharge-intro-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.recharge-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recharge-label {
  font-size: 14px;
  color: #334155;
  font-weight: 600;
}

.recharge-actions {
  display: flex;
  justify-content: center;
}

.recharge-create-btn {
  width: 100%;
  height: 40px;
  border-radius: 10px;
  font-weight: 600;
}

.custom-amount-wrap {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-amount-input {
  flex: 1;
}

.custom-amount-unit {
  font-size: 13px;
  color: #64748b;
  flex-shrink: 0;
}

.custom-amount-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.qr-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #fcfdff;
}

.qr-img {
  width: 240px;
  height: 240px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 8px;
}

.qr-tip {
  color: #64748b;
  font-size: 12px;
  text-align: center;
  word-break: break-all;
}

.qr-state {
  margin-top: 2px;
  font-size: 12px;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 4px 10px;
}

:deep(.recharge-dialog .el-dialog) {
  border-radius: 14px;
  overflow: hidden;
}

:deep(.recharge-dialog .el-dialog__header) {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #eef2f7;
}

:deep(.recharge-dialog .el-dialog__title) {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

:deep(.recharge-dialog .el-dialog__body) {
  padding: 16px 18px 20px;
}

:deep(.recharge-amount-group.el-radio-group) {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

:deep(.recharge-amount-group .el-radio-button) {
  width: 100%;
}

:deep(.recharge-amount-group .el-radio-button__inner) {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #dbe3ef;
  height: 38px;
  line-height: 38px;
  padding: 0;
  color: #334155;
  font-weight: 600;
  box-shadow: none;
}

:deep(.recharge-amount-group .el-radio-button.is-active .el-radio-button__inner) {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
</style>
